import type { AdminV2CommunicationContactScopeSelection } from "@/types/adminV2/communicationContactScope";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  PersonalCommunicationDraft,
  PersonalDraftList,
  PersonalDraftReconciliation,
  PersonalDraftSaveInput,
  PersonalDraftSaveResult,
} from "@/types/adminV2/personalCommunicationDraft";
/** One existing API slice; ownerKey partitions private cached copy and stays off the wire. */
export const buildAdminV2PersonalDraftEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  listAdminV2PersonalCommunicationDrafts: builder.query<
    PersonalDraftList,
    {
      personId: string;
      organisationId: string | null;
      ownerKey: string;
      contactScope?: AdminV2CommunicationContactScopeSelection;
    }
  >({
    query: ({ personId, organisationId, contactScope }) => ({
      url: "/admin/v2/messaging/personal-drafts",
      params: {
        personId,
        organisationId: organisationId ?? "none",
        ...(contactScope ? { contactScope: JSON.stringify(contactScope) } : {}),
      },
    }),
    providesTags: (_result, _error, args) => [
      {
        type: "AdminV2PersonalCommunicationDrafts",
        id: `${args.ownerKey}:${args.personId}:${args.organisationId}`,
      },
    ],
    keepUnusedDataFor: 0,
  }),
  saveAdminV2PersonalCommunicationDraft: builder.mutation<
    PersonalDraftSaveResult,
    { body: PersonalDraftSaveInput }
  >({
    query: ({ body }) => ({
      url: "/admin/v2/messaging/personal-drafts",
      method: "POST",
      data: body,
    }),
    invalidatesTags: (result) => [
      { type: "AdminV2AuditLog", id: "LIST" },
      ...(result?.draft
        ? [
            {
              type: "AdminV2PersonalCommunicationDrafts",
              id: `${result.draft.actorStaffUserId}:${result.draft.personId}:${result.draft.organisationId}`,
            },
          ]
        : []),
    ],
  }),
  getAdminV2PersonalCommunicationDraft: builder.query<
    { draft: PersonalCommunicationDraft },
    { draftId: string; organisationId: string | null; ownerKey: string }
  >({
    query: ({ draftId, organisationId }) => ({
      url: `/admin/v2/messaging/personal-drafts/${encodeURIComponent(draftId)}`,
      params: { organisationId: organisationId ?? "none" },
    }),
    keepUnusedDataFor: 0,
  }),
  reconcileAdminV2PersonalCommunicationDraft: builder.mutation<
    PersonalDraftReconciliation,
    {
      body: {
        clientRequestId: string;
        personId: string;
        organisationId: string | null;
      };
    }
  >({
    query: ({ body }) => ({
      url: "/admin/v2/messaging/personal-drafts/reconcile",
      method: "POST",
      data: body,
    }),
    invalidatesTags: (result) =>
      result?.state === "saved"
        ? [
            {
              type: "AdminV2PersonalCommunicationDrafts",
              id: `${result.draft.actorStaffUserId}:${result.draft.personId}:${result.draft.organisationId}`,
            },
          ]
        : [],
  }),
});
